// src/admin/pages/UsersPage.jsx
//
// User administration page. Route: /users.
//
// Same structure as admin/pages/RolesPage.jsx and PermissionsPage.jsx:
// PageHeader with the action on the right, a StatTile summary row, one
// DataTable, create/edit behind a modal. Replaces pages/UsersPage.jsx (a
// hand-rolled <table>, an always-open inline form, two window.confirm calls).
//
// styles/UsersPage.css is deliberately NOT deleted with this page: four other
// pages (AccountsPage, AccountDetailPage, EngagementsPage, EngagementDetailPage)
// import it directly for its .btn/.badge/.table-wrap/.users-table/.role-grid
// primitives.
//
// The page does not re-check admin:manage_users — AppRoutes already gates the
// route on it, so reaching here implies holding it, and re-testing would only
// add a way for the actions to vanish for a legitimate admin. The finer
// admin:grant_platform_role check below is a real distinction and is applied.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Users, Plus, UserCheck, UserX } from "lucide-react";
import {
  PageHeader,
  Button,
  EmptyState,
  DataTable,
  StatusBadge,
  StatTile,
  Alert,
  ConfirmModal,
  SkeletonTable,
} from "../../shared/ui";
import {
  listUsers,
  listAssignableRoles,
  createUser,
  updateUser,
  activateUser,
  deactivateUser,
} from "../api/usersApi";
import { useAuth } from "../../context/AuthContext";
import UserFormModal from "../components/UserFormModal";

export default function UsersPage() {
  const { hasGlobalPermission } = useAuth();
  // Global, not scoped to whatever project/account is selected elsewhere —
  // granting a PLATFORM-level role, or deactivating someone who holds one, is
  // never account/project-scoped.
  const canGrantPlatformRole = hasGlobalPermission("admin:grant_platform_role");

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState(null);

  // null = closed. { user: null } = create, { user: rec } = edit.
  const [formTarget, setFormTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setUsers(await listUsers());
    } catch (err) {
      setLoadError(err?.message || "Failed to load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRoles = useCallback(async () => {
    setRolesLoading(true);
    setRolesError(null);
    try {
      setRoles(await listAssignableRoles({ includePlatform: canGrantPlatformRole }));
    } catch (err) {
      setRolesError(err?.message || "Failed to load assignable roles.");
    } finally {
      setRolesLoading(false);
    }
  }, [canGrantPlatformRole]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const handleSubmit = useCallback(
    async (values) => {
      const editing = formTarget?.user;
      const saved = editing
        ? await updateUser(editing.id, values)
        : await createUser(values);
      await refresh();
      return saved;
    },
    [formTarget, refresh]
  );

  const handleDeactivate = useCallback(async () => {
    await deactivateUser(deactivateTarget.id);
    setDeactivateTarget(null);
    await refresh();
  }, [deactivateTarget, refresh]);

  // Activate runs without a confirmation step: it restores access rather than
  // removing it, and is immediately undoable by deactivating again. The old
  // page confirmed both directions; only the destructive one still does.
  const handleActivate = useCallback(
    async (user) => {
      setActionError(null);
      setBusyId(user.id);
      try {
        await activateUser(user.id);
        await refresh();
      } catch (err) {
        setActionError(err?.message || "Could not activate the user.");
      } finally {
        setBusyId(null);
      }
    },
    [refresh]
  );

  const activeCount = users.filter((u) => u.isActive).length;
  const unassignedCount = users.filter((u) => u.roles.length === 0).length;

  const columns = useMemo(
    () => [
      {
        accessorKey: "username",
        header: "User",
        cell: ({ row }) => (
          <div className="min-w-0">
            <span className="block font-medium text-ink">{row.original.username}</span>
            <span className="block text-xs text-muted mt-0.5">{row.original.email}</span>
          </div>
        ),
      },
      {
        // Sorts by count, not by a joined string — ordering by "how many
        // roles" is the useful comparison. The old page rendered the raw
        // comma-joined names with no level context.
        id: "roleCount",
        header: "Roles",
        accessorFn: (row) => row.roles.length,
        width: 280,
        cell: ({ row }) => {
          const { roles: held } = row.original;
          if (held.length === 0) {
            return (
              <span className="text-xs text-amber-700 font-medium">
                No role — every check fails
              </span>
            );
          }
          return (
            <div className="flex items-center gap-1.5 flex-wrap">
              {held.map((r) => (
                <span
                  key={r.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-slate-700 text-[11px] font-semibold"
                  title={`${r.name} — ${r.level} level`}
                >
                  {r.name}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        accessorKey: "isActive",
        header: "Status",
        width: 120,
        cell: ({ getValue }) => (
          <StatusBadge map="record" status={getValue() ? "active" : "inactive"} size="sm" />
        ),
      },
      {
        id: "actions",
        header: "",
        align: "right",
        width: 180,
        sortAllowed: false,
        cell: ({ row }) => {
          const user = row.original;
          // The backend refuses a non-platform-tier caller deactivating a
          // platform-tier user (SetUserActivationCommand.caller_is_platform_tier).
          // Hiding the action is clearer than offering it and returning 403.
          const canDeactivate = canGrantPlatformRole || !user.isPlatformTier;

          return (
            <div className="flex items-center justify-end gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFormTarget({ user })}
              >
                Edit
              </Button>
              {user.isActive ? (
                canDeactivate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => setDeactivateTarget(user)}
                  >
                    Deactivate
                  </Button>
                )
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-green-700 hover:text-green-800"
                  loading={busyId === user.id}
                  onClick={() => handleActivate(user)}
                >
                  Activate
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [canGrantPlatformRole, busyId, handleActivate]
  );

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle="People who can sign in to this organization, and the roles they hold."
        actions={
          <Button variant="primary" onClick={() => setFormTarget({ user: null })}>
            <Plus size={15} /> New user
          </Button>
        }
      />

      {!loading && !loadError && users.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <StatTile icon={Users} label="Total Users" value={users.length} />
          <StatTile icon={UserCheck} label="Active" value={activeCount} />
          <StatTile
            icon={UserX}
            label="Without a Role"
            value={unassignedCount}
            hint={unassignedCount > 0 ? "Can sign in, but nothing else" : undefined}
          />
        </div>
      )}

      {actionError && (
        <Alert variant="danger" title="Something went wrong" className="mb-4">
          {actionError}
        </Alert>
      )}

      {/* Surfaced on the page, not only inside the modal: the role picker is
          unusable until this loads, so saying so here saves opening the form
          to find out. */}
      {rolesError && (
        <Alert variant="danger" title="Assignable roles unavailable" className="mb-4">
          {rolesError} Users are listed below, but creating or editing one is disabled
          until this loads.{" "}
          <button type="button" onClick={loadRoles} className="underline font-semibold">
            Retry
          </button>
        </Alert>
      )}

      {loading ? (
        <SkeletonTable rows={5} columns={4} />
      ) : loadError ? (
        <EmptyState
          icon={Users}
          title="Couldn't load users"
          description={loadError}
          action={
            <Button variant="secondary" onClick={refresh}>
              Try again
            </Button>
          }
        />
      ) : users.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No users yet"
          description="Pre-create someone's record with their Google address, and their first sign-in will be matched to it."
          action={
            <Button variant="secondary" onClick={() => setFormTarget({ user: null })}>
              <Plus size={15} /> New user
            </Button>
          }
        />
      ) : (
        <DataTable table={{ columns, rows: users }} canSearch />
      )}

      {/* Keyed so switching between create and editing a different row
          re-seeds the form's initial state instead of keeping stale input. */}
      <UserFormModal
        key={formTarget?.user?.id ?? "new"}
        open={Boolean(formTarget)}
        user={formTarget?.user || null}
        roles={roles}
        rolesLoading={rolesLoading}
        rolesError={rolesError}
        onClose={() => setFormTarget(null)}
        onSubmit={handleSubmit}
      />

      <ConfirmModal
        open={Boolean(deactivateTarget)}
        entityLabel="user"
        name={deactivateTarget?.username}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={handleDeactivate}
      >
        They will be refused at login from the next attempt, and their record and roles
        are kept. This can be undone with Activate.
      </ConfirmModal>
    </div>
  );
}
